if (!customElements.get("quick-add")) {
  customElements.define(
    "quick-add",
    class QuickAdd extends HTMLElement {
      getFocusableElements(parent) {
        const focusableElementsSelector =
          "button:not(.wt-product__sticky-buy__button), [href], input:not(.disabled):not([type='hidden']), select, .close-btn";
        const focusableElements = () =>
          Array.from(parent.querySelectorAll(focusableElementsSelector)).filter(
            (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
          );
        return {
          focusableElements,
          first: focusableElements()[0],
          last: focusableElements()[focusableElements().length - 1],
        };
      }

      connectedCallback() {
        this.add_button = this.querySelector("button");
        this.isDrawerOpen = false;
        this.isMobile = window.innerWidth < 768;
        if (this.add_button)
          this.add_button.addEventListener("click", (e) => {
            const button = e.currentTarget;
            const cardLink = button.closest(".card__picture-container")?.querySelector("a.card");
            this.product_url = this.add_button.getAttribute("data-product-url");
            this.currentTrigger = cardLink;
            this.fetchProduct(this.product_url);
          });

        this.handleInteractionOutside = this.handleInteractionOutside.bind(this);
        this.getFocusableElements = this.getFocusableElements.bind(this);
        this.lastFeaturedImage;
        this.firstMedia;
        this.hasFirstMediaImg;
        this.fetchProduct = this.fetchProduct.bind(this);
        this.galleryObserver = this.galleryObserver.bind(this);
        this.observerCallback = this.observerCallback.bind(this);
        this.disconnectObserver = this.disconnectObserver.bind(this);
        this.removeButtonEventListener = this.removeButtonEventListener.bind(this);
      }

      fetchProduct(product_url) {
        this.lastFeaturedImage = null;
        this.firstMedia = null;
        this.hasFirstMediaImg = null;
        this.quick_add = document.querySelector(".wt__quick-buy");
        this.quick_add_container = document.querySelector(".wt__quick-buy__container");
        this.quick_add_wrapper = document.querySelector(".wt__quick-buy--wrapper");
        this.close_button = this.quick_add.querySelector(".close-btn");
        this.loader = this.quick_add.querySelector(".wt__quick-buy-loader");
        this.page_overlay = document.querySelector(".wt__quick-buy--page-overlay");
        this.body = document.body;

        const controller = new AbortController();
        const signal = controller.signal;

        // Clean up previous
        this.quick_add_wrapper.querySelectorAll(".wt-product").forEach((p) => p.remove());
        this.quick_add_container.querySelector(".wt__quick-buy__sticky-footer")?.remove();

        this.quick_add.classList.remove("hidden");
        this.body.classList.add("quick-buy-page-overlay");
        this.page_overlay.classList.add("wt__quick-buy--page-overlay--open");
        this.quick_add_container.classList.add("wt__quick-buy__container--open");

        this.close_button.addEventListener("click", () => {
          controller.abort();
          this.closeCart();
          this.close_button.setAttribute("tabindex", "-1");
          this.currentTrigger?.focus();
        });

        this.quick_add_container.addEventListener("keydown", (e) => {
          const isTabPressed = e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
          const { first, last } = this.getFocusableElements(this.quick_add_container);
          if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") this.close_button.click();
          if (isTabPressed) {
            if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
            else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
          }
        });

        fetch(product_url, { signal })
          .then((res) => res.text())
          .then((res) => {
            this.loader.classList.add("hidden");
            const htmlDocument = new DOMParser().parseFromString(res, "text/html");
            this.productMain = htmlDocument.querySelector(".wt-product__main");
            this.productCard = htmlDocument.querySelector(".wt-product");
            this.productInfo = htmlDocument.querySelector(".wt-product__info");

            this.productMain.querySelectorAll(".scroll-trigger.animate--slide-in").forEach((el) =>
              el.classList.remove("scroll-trigger", "animate--slide-in")
            );

            const productGallery = this.productCard.querySelector("gallery-section");

            // Extract metafields BEFORE removeElements strips anything
            this._metafieldData = this._extractMetafields(htmlDocument);

            // Strip sticky-buy-button from the FULL parsed document first —
            // it lives as a sibling of .wt-product, not inside it, so targeting
            // productCard alone misses it. When it reaches the live DOM it
            // renders position:fixed to the bottom of the viewport.
            this.removeElements(
              htmlDocument.body,
              "sticky-buy-button",
              ".wt-product__sticky-buy",
            );

            this.removeElements(
              this.productCard,
              "collapsible-section", "[not-quick-add]", ".product__inventory",
              ".share-icons__container", "#gallery-loader", "pickup-availability",
              ".wt-product__sku", ".wt-product__feature-icons", "gallery-section",
              ".wt-product__related", ".wt-product__related-products",
              ".product__related-products", ".product-recommendations",
              "[data-metafield='custom.related_products']", ".metafields-variants",
              ".wt-product__also-available", ".product-metafields-data",
              "sticky-buy-button", ".wt-product__sticky-buy",
            );

            const newVariantId = this.productCard.querySelector("variant-options");
            if (newVariantId) {
              newVariantId.dataset.updateUrl = "false";
              this.updateAttribute(newVariantId, "data-original-section", newVariantId.getAttribute("data-section"));
              this.updateAttribute(newVariantId, "data-section", `quick-${newVariantId.getAttribute("data-section")}`);
            }

            productGallery.style.display = "none";
            this.images = productGallery.querySelectorAll("img");
            this.updateAttribute(productGallery, "id", `MediaGallery-${newVariantId?.getAttribute("data-section")}`);

            const firstImageFromGallery = productGallery.querySelector("li");
            if (firstImageFromGallery) {
              this.firstMedia = firstImageFromGallery;
              const playBtn = this.firstMedia.querySelector(".model-btn");
              if (playBtn) playBtn.setAttribute("tabindex", "0");
              const video = this.firstMedia.querySelector("video");
              if (video) video.setAttribute("tabindex", "0");
              this.hasFirstMediaImg = Boolean(firstImageFromGallery.querySelector("img"));
            }

            firstImageFromGallery?.querySelector("img")?.removeAttribute("srcset");

            const imageContainer = document.createElement("div");
            imageContainer.setAttribute("class", "product-image");
            const productDetails = document.createElement("div");
            productDetails.setAttribute("class", "wt-product__details");
            const productAbout = document.createElement("div");
            productAbout.setAttribute("class", "product-info");

            const title = this.productInfo.querySelector(".wt-product__name");
            if (title) {
              const a = document.createElement("a");
              a.textContent = title.textContent;
              a.href = this.product_url;
              title.innerHTML = "";
              title.appendChild(a);
              title.setAttribute("tabindex", "0");
            }

            this.productInfo.querySelectorAll(
              ".wt-product__brand, .wt-product__name, .wt-rating, .wt-product__price, .product__tax"
            ).forEach((el) => productAbout.appendChild(el));

            imageContainer.innerHTML = firstImageFromGallery?.innerHTML || "";
            productDetails.appendChild(imageContainer);
            productDetails.appendChild(productAbout);
            this.productInfo.prepend(productDetails);

            this.galleryObserver();

            const mainProductPrice = this.productCard.querySelector(".wt-product__price");
            this.updateAttribute(mainProductPrice, "id", `price-${newVariantId?.getAttribute("data-section")}`);

            const allFieldSets = this.productCard.querySelectorAll("fieldset");
            this.updateFieldSets(allFieldSets);

            const addBtn = this.productCard.querySelector('button[name="add"]');
            this.updateAttribute(addBtn, "id", `ProductSubmitButton-${newVariantId?.getAttribute("data-section")}`);

            const form = this.productCard.querySelector('form[method="post"][data-type="add-to-cart-form"]');
            this.updateAttribute(form, "id", `product-form-${newVariantId?.getAttribute("data-section")}`);

            // Safe insert into live DOM
            // Also strip any inline <script> tags from the card — page-level scripts
            // (like the ingredients slider) will error or conflict when re-run in this context.
            const viewAllContainer = this.quick_add_wrapper.querySelector(".wt__quick-buy__view-all-container");
            const cardNode = document.createElement("div");
            cardNode.innerHTML = this.productCard.outerHTML;
            // Remove scripts from the card clone before inserting
            cardNode.querySelectorAll("script").forEach((s) => s.remove());
            const insertedCard = cardNode.firstElementChild;

            if (viewAllContainer) {
              this.quick_add_wrapper.insertBefore(insertedCard, viewAllContainer);
            } else {
              this.quick_add_wrapper.appendChild(insertedCard);
            }

            const giftCardInput = this.quick_add_wrapper.querySelector('[name="properties[__shopify_send_gift_card_to_recipient]"]');
            if (giftCardInput) giftCardInput.removeAttribute("disabled");

            // 1. Build sticky footer first (lifts qty-counter + ATC button out)
            this.buildStickyFooter();
            // 2. Inject accordion into what remains
            this.injectMetafieldAccordion();
            // 3. Wire view-all link
            this.addViewAllDetailsButton();

            this.isDrawerOpen = true;
            publish(PUB_SUB_EVENTS.quickBuyDrawerOpen, { source: "quick-add" });

            const variantOptions = this.quick_add_wrapper.querySelector("variant-options");
            if (variantOptions && typeof variantOptions.initialize === "function") {
              variantOptions.initialize();
            }

            this.close_button.setAttribute("tabindex", "0");
            this.close_button.focus();
            document.addEventListener("click", this.handleInteractionOutside);
          })
          .catch((error) => {
            if (error.name === "AbortError") console.error("Fetch aborted");
            else console.error("Fetch error:", error);
          });
      }

      _extractMetafields(htmlDocument) {
        const get = (selector) => {
          const el = htmlDocument.querySelector(selector);
          return el ? el.innerHTML.trim() : null;
        };
        return {
          description: get('[data-metafield="product.description"]') || get(".wt-product__description") || get(".product__description"),
          ingredients: get('[data-metafield="my_fields.ingredients"]'),
          preparation: get('[data-metafield="my_fields.preparation_instruction"]'),
        };
      }

      buildStickyFooter() {
        this.quick_add_container.querySelector(".wt__quick-buy__sticky-footer")?.remove();

        const liveCard = this.quick_add_wrapper.querySelector(".wt-product");
        if (!liveCard) return;

        // From the live HTML we can see:
        // - quantity-counter is inside .wt-product__add-to-cart_form--wrapper
        // - button[name="add"] is also inside .wt-product__add-to-cart_form--wrapper
        // - the whole wrapper is inside .wt-product__add-to-cart_form.main-product__buy-buttons--container
        const buyButtonsContainer = liveCard.querySelector(
          ".wt-product__add-to-cart_form.main-product__buy-buttons--container, .wt-product__add-to-cart_form--wrapper"
        );

        // Grab qty counter — Wonder theme uses <quantity-counter> custom element
        const qtyEl = liveCard.querySelector("quantity-counter");
        // Grab ATC button
        const atcBtn = liveCard.querySelector('button[name="add"]');
        // View-all container
        const viewAllContainer = this.quick_add_wrapper.querySelector(".wt__quick-buy__view-all-container");

        const footer = document.createElement("div");
        footer.className = "wt__quick-buy__sticky-footer";

        const actionsRow = document.createElement("div");
        actionsRow.className = "wt__quick-buy__sticky-actions";

        if (qtyEl) actionsRow.appendChild(qtyEl);
        if (atcBtn) actionsRow.appendChild(atcBtn);
        footer.appendChild(actionsRow);

        const viewAllRow = document.createElement("div");
        viewAllRow.className = "wt__quick-buy__view-all-row";
        if (viewAllContainer) viewAllRow.appendChild(viewAllContainer);
        footer.appendChild(viewAllRow);

        // Append footer to container — CSS makes it sticky at bottom
        this.quick_add_container.appendChild(footer);

        // Hide the now-empty buy buttons container if nothing left in it
        if (buyButtonsContainer && !buyButtonsContainer.querySelector("button, input[type='submit']")) {
          buyButtonsContainer.style.display = "none";
        }
      }

      addViewAllDetailsButton() {
        const footer = this.quick_add_container.querySelector(".wt__quick-buy__sticky-footer");
        if (!footer) return;
        const vac = footer.querySelector(".wt__quick-buy__view-all-container");
        if (!vac) return;
        const link = vac.querySelector("a");
        if (link) {
          link.href = this.product_url;
          link.setAttribute("tabindex", "0");

          // ── GA4 via GTM: cross-sell view all details click ───────────────
          link.addEventListener("click", () => {
            const liveCard = this.quick_add_wrapper.querySelector(".wt-product");
            const productName = liveCard?.querySelector(".wt-product__name")?.textContent?.trim() || "";
            const productPrice = liveCard?.querySelector(".price-item--regular, .price-item--last")?.textContent?.trim() || "";
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "cross_sell_view_all_details",
              cross_sell_product_name: productName,
              cross_sell_product_url: this.product_url,
              cross_sell_product_price: productPrice,
            });
          }, { once: true });
        }
        vac.classList.remove("hidden");
      }

      hideViewAllDetailsButton() {
        const footer = this.quick_add_container?.querySelector(".wt__quick-buy__sticky-footer");
        if (footer) {
          const vac = footer.querySelector(".wt__quick-buy__view-all-container");
          if (vac) { vac.classList.add("hidden"); this.quick_add_wrapper.appendChild(vac); }
          footer.remove();
        } else {
          this.quick_add_wrapper?.querySelector(".wt__quick-buy__view-all-container")?.classList.add("hidden");
        }
      }

      injectMetafieldAccordion() {
        const liveCard = this.quick_add_wrapper.querySelector(".wt-product");
        if (!liveCard) return;
        const productInfo = liveCard.querySelector(".wt-product__info");
        if (!productInfo) return;

        productInfo.querySelector(".wt__quick-buy__accordion")?.remove();

        // Scrub "also available in" by text content
        liveCard.querySelectorAll("*").forEach((el) => {
          if (el.children.length > 0) return;
          const text = el.textContent?.trim().toLowerCase();
          if (text === "also available in" || text === "you may also like") {
            let parent = el.parentElement;
            while (parent && parent !== liveCard) {
              if (parent.classList?.length > 0) { parent.remove(); return; }
              parent = parent.parentElement;
            }
            el.remove();
          }
        });

        const data = this._metafieldData || {};
        const fields = [
          { label: "Description",              content: data.description },
          { label: "Ingredients",              content: data.ingredients },
          { label: "Preparation Instructions", content: data.preparation },
        ].filter((f) => f.content && f.content.trim().length > 0);

        if (fields.length === 0) return;

        const accordion = document.createElement("div");
        accordion.className = "wt__quick-buy__accordion";

        fields.forEach((field) => {
          const item = document.createElement("div");
          item.className = "wt__quick-buy__accordion-item";

          const trigger = document.createElement("button");
          trigger.type = "button";
          trigger.className = "wt__quick-buy__accordion-trigger";
          trigger.innerHTML = `<span>${field.label}</span><span class="wt__quick-buy__accordion-icon">+</span>`;

          const body = document.createElement("div");
          body.className = "wt__quick-buy__accordion-body";
          body.hidden = true;
          body.innerHTML = `<div class="wt__quick-buy__accordion-content">${field.content}</div>`;

          trigger.addEventListener("click", () => {
            const isOpen = !body.hidden;
            accordion.querySelectorAll(".wt__quick-buy__accordion-body").forEach((b) => { b.hidden = true; });
            accordion.querySelectorAll(".wt__quick-buy__accordion-trigger").forEach((t) => {
              t.classList.remove("is-open");
              t.querySelector(".wt__quick-buy__accordion-icon").textContent = "+";
            });
            if (!isOpen) {
              body.hidden = false;
              trigger.classList.add("is-open");
              trigger.querySelector(".wt__quick-buy__accordion-icon").textContent = "−";
            }
          });

          item.appendChild(trigger);
          item.appendChild(body);
          accordion.appendChild(item);
        });

        // Append at end of productInfo — qty+ATC already moved to sticky footer
        productInfo.appendChild(accordion);
      }

      galleryObserver() {
        const config = { attributes: true, childList: true, subtree: true, characterData: true };
        this.observer = new MutationObserver(this.observerCallback);
        this.observer.observe(this.quick_add_wrapper, config);
      }

      observerCallback(mutations) {
        for (const mutation of mutations) {
          if (mutation.target.localName === "variant-options") {
            this.quick_add = document.querySelector(".wt__quick-buy");
            const variantOptions = this.quick_add.querySelector("variant-options");
            const imageContainer = this.quick_add.querySelector(".product-image");
            const image = imageContainer?.querySelector("img");
            const formInput = this.quick_add.querySelector('form input[name="id"]');
            if (variantOptions.getAttribute("data-variant-id")) {
              if (formInput) {
                formInput.value = variantOptions.getAttribute("data-variant-id");
                formInput.setAttribute("value", variantOptions.getAttribute("data-variant-id"));
              }
            }
            if (variantOptions.getAttribute("data-featured-image") && this.lastFeaturedImage !== variantOptions.getAttribute("data-featured-image")) {
              image?.setAttribute("src", variantOptions.getAttribute("data-featured-image"));
              image?.removeAttribute("srcset");
              this.lastFeaturedImage = variantOptions.getAttribute("data-featured-image");
              if (imageContainer?.children[0]?.nodeName !== "IMG" && imageContainer?.children[0]?.nodeName !== "A") {
                const newImage = document.createElement("img");
                newImage.setAttribute("class", "wt-product__img wt-product__img--zoom-cursor");
                newImage.setAttribute("src", variantOptions.getAttribute("data-featured-image"));
                newImage.removeAttribute("srcset");
                if (imageContainer) { imageContainer.innerHTML = ""; imageContainer.appendChild(newImage); }
              } else {
                image?.setAttribute("src", variantOptions.getAttribute("data-featured-image"));
                image?.removeAttribute("srcset");
              }
            } else {
              const hasFirstMediaModel = this.firstMedia?.querySelector("model-element");
              if (!this.hasFirstMediaImg || hasFirstMediaModel)
                if (imageContainer) imageContainer.innerHTML = this.firstMedia?.innerHTML;
            }
            break;
          }
        }
      }

      handleInteractionOutside(event) {
        if (this.isDrawerOpen && event.target === this.page_overlay) this.closeCart();
      }

      closeCart() {
        publish(PUB_SUB_EVENTS.quickBuyDrawerClose, { source: "quick-add" });
        const loader = this.quick_add.querySelector(".wt__quick-buy-loader");
        loader.classList.remove("hidden");
        this.quick_add.querySelector(".wt-product")?.remove();
        this.body.classList.remove("quick-buy-page-overlay");
        this.page_overlay.classList.remove("wt__quick-buy--page-overlay--open");
        this.quick_add_container.classList.remove("wt__quick-buy__container--open");
        this.isDrawerOpen = false;
        this.hideViewAllDetailsButton();
        document.removeEventListener("click", this.handleInteractionOutside);
        this.disconnectObserver();
        this.removeButtonEventListener();
      }

      removeElements(element, ...selectors) {
        selectors.forEach((selector) => {
          try { element.querySelectorAll(selector).forEach((el) => el.remove()); } catch (e) {}
        });
      }

      updateAttribute(element, attribute, value) {
        if (element) element.setAttribute(attribute, value);
      }

      updateFieldSets(fieldSets) {
        fieldSets.forEach((fieldset) => {
          fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
            this.updateAttribute(radio, "id", `quick-${radio.getAttribute("id")}`);
            this.updateAttribute(radio, "form", `product-form-${radio.getAttribute("id")}`);
          });
          fieldset.querySelectorAll("label").forEach((label) => {
            this.updateAttribute(label, "for", `quick-${label.getAttribute("for")}`);
          });
        });
      }

      disconnectObserver() { this.observer?.disconnect(); }
      removeButtonEventListener() {
        if (this.add_button)
          this.add_button.removeEventListener("click", () => this.fetchProduct(this.product_url));
      }
      disconnectedCallback() { this.disconnectObserver(); this.removeButtonEventListener(); }
    },
  );
}
